import { displayName } from '../../types';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Chip } from '@mui/material';
import { apiService } from '../../services/api';

const COLORS = ['#1976d2','#7b1fa2','#388e3c','#f57c00','#d32f2f','#0288d1','#512da8'];

const OrgNode: React.FC<{ emp: any; allEmps: any[]; depth: number }> = ({ emp, allEmps, depth }) => {
  const reports = allEmps.filter(e => e.managerId === emp.id);
  const color = COLORS[depth % COLORS.length];
  const initials = `${(emp.nickname || emp.firstName)?.[0] || ''}${emp.lastName?.[0] || ''}`;

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box sx={{
        bgcolor: color, color: 'white', borderRadius: 2, px: 2, py: 1.5,
        minWidth: 150, textAlign: 'center', boxShadow: 2, cursor: 'default',
        '&:hover': { opacity: 0.9, transform: 'scale(1.02)', transition: 'all 0.15s' }
      }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.5, fontWeight: 'bold' }}>
          {initials}
        </Box>
        <Typography variant="caption" fontWeight="bold" display="block">{displayName(emp)}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }} display="block">{emp.position?.title || emp.role || '—'}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }} display="block">{emp.department?.name}</Typography>
      </Box>
      {reports.length > 0 && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box sx={{ width: '2px', height: 24, bgcolor: '#ccc' }} />
          <Box display="flex" gap={3} alignItems="flex-start" position="relative">
            {reports.length > 1 && (
              <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', height: '2px', bgcolor: '#ccc', width: `calc(100% - 75px)` }} />
            )}
            {reports.map(r => (
              <Box key={r.id} display="flex" flexDirection="column" alignItems="center">
                <Box sx={{ width: '2px', height: 24, bgcolor: '#ccc' }} />
                <OrgNode emp={r} allEmps={allEmps} depth={depth + 1} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const OrgChart: React.FC = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      apiService.getEmployees().catch(() => []),
      apiService.getEntities().catch(() => []),
      apiService.getDepartments().catch(() => []),
    ]).then(([emps, ents, depts]) => {
      setEmployees(emps.filter((e: any) => e.employmentStatus === 'active'));
      setEntities(ents);
      setDepartments(depts);
      setLoading(false);
    }).catch(() => { setError('Failed to load data'); setLoading(false); });
  }, []);

  const filtered = employees.filter(e => {
    if (filterEntity && e.entityId !== filterEntity) return false;
    if (filterDept && e.departmentId !== filterDept) return false;
    return true;
  });

  const roots = filtered.filter(e => !e.managerId || !filtered.find(x => x.id === e.managerId));

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Org Chart</Typography>
        <Chip label={`${filtered.length} employees`} color="primary" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Entity</InputLabel>
          <Select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} label="Filter by Entity">
            <MenuItem value="">All Entities</MenuItem>
            {entities.map((e: any) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Department</InputLabel>
          <Select value={filterDept} onChange={e => setFilterDept(e.target.value)} label="Filter by Department">
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((d: any) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ p: 4, overflowX: 'auto', overflowY: 'auto', minHeight: 300 }}>
        {roots.length === 0 ? (
          <Typography color="text.secondary" textAlign="center">No active employees found. Add employees with reporting lines to see the org chart.</Typography>
        ) : (
          <Box display="flex" gap={6} justifyContent="center" flexWrap="wrap">
            {roots.map(emp => <OrgNode key={emp.id} emp={emp} allEmps={filtered} depth={0} />)}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OrgChart;
